'use client';

import React from 'react';
import { useGetEnvironment } from './use-get-environment';

// ✅ Move React.lazy() to module level to prevent infinite rerenders
const ReactRouterLink = React.lazy(() =>
  import('react-router').then((mod) => ({ default: mod.Link })),
);

const NextLink = React.lazy(() => import('next/link'));

export const UniversalLink = ({
  href,
  to,
  children,
  nextjs,
  ...props
}: {
  href?: string;
  to?: string;
  children: React.ReactNode;
  nextjs?: boolean;
  [key: string]: unknown;
}) => {
  const { isVite, isNextJs } = useGetEnvironment();
  const linkPath = href || to;

  if (!linkPath) return <span {...props}>{children}</span>;

  // ---------------- VITE ----------------
  if (isVite) {
    const hubNextUrl =
      typeof import.meta !== 'undefined' &&
      (import.meta as unknown as { env: { VITE_HUB_NEXT_URL?: string } }).env
        ?.VITE_HUB_NEXT_URL;

    if (!hubNextUrl) {
      throw new Error(`VITE_HUB_NEXT_URL is not set in Vite environment`);
    }

    const normalizedBase = hubNextUrl.endsWith('/')
      ? hubNextUrl
      : `${hubNextUrl}/`;
    const normalizedPath = linkPath.startsWith('/')
      ? linkPath.slice(1)
      : linkPath;
    const hubNextLinkPath = new URL(normalizedPath, normalizedBase).toString();
    const finalLinkPath = nextjs ? hubNextLinkPath : linkPath;

    return (
      <React.Suspense fallback={<span {...props}>{children}</span>}>
        <ReactRouterLink
          to={finalLinkPath}
          {...(nextjs && { reloadDocument: true })}
          {...props}
        >
          {children}
        </ReactRouterLink>
      </React.Suspense>
    );
  }

  // ---------------- NEXT.JS ----------------
  if (isNextJs) {
    const hubUrl = process?.env?.NEXT_PUBLIC_HUB_URL;
    if (!hubUrl) {
      throw new Error(`NEXT_PUBLIC_HUB_URL is not set in Next.js environment`);
    }

    const normalizedBase = hubUrl.endsWith('/') ? hubUrl : `${hubUrl}/`;
    const normalizedPath = linkPath.startsWith('/')
      ? linkPath.slice(1)
      : linkPath;
    const hubLinkPath = new URL(normalizedPath, normalizedBase).toString();
    const finalLinkPath = !nextjs ? hubLinkPath : linkPath;

    return (
      <React.Suspense fallback={<span {...props}>{children}</span>}>
        <NextLink
          href={finalLinkPath}
          {...(!nextjs && { replace: true })}
          {...props}
        >
          {children}
        </NextLink>
      </React.Suspense>
    );
  }

  // Fallback: plain span
  return <span {...props}>{children}</span>;
};

export const useConditionalLink = () => {
  return UniversalLink;
};
